import { useState, useMemo, useEffect, useRef } from 'react'
import { useProduits } from '@/hooks/useProduits'
import { Search, Package, ChevronDown } from 'lucide-react'
import type { Produit } from '@/types/database-real'

interface ProductSelectProps {
  value?: string // ID du produit sélectionné
  onChange: (produit: Produit | null) => void
  error?: string
}

export function ProductSelect({ value, onChange, error }: ProductSelectProps) {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { produits, isLoading } = useProduits({ search })

  // Produit sélectionné
  const selectedProduit = useMemo(() => {
    return produits.find((p) => p.id === value) || null
  }, [produits, value])

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (produit: Produit) => {
    onChange(produit)
    setIsOpen(false)
    setSearch('')
  }

  const handleClear = () => {
    onChange(null)
    setSearch('')
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-brown-700 mb-1">
        Produit / Service
      </label>

      {/* Bouton de sélection */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2 bg-white border rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-mint-500 ${
          error ? 'border-red-500' : 'border-beige-300'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Package className="w-4 h-4 text-brown-400 flex-shrink-0" />
          {selectedProduit ? (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {selectedProduit.code && (
                  <span className="font-mono font-semibold text-mint-700 text-sm">
                    {selectedProduit.code}
                  </span>
                )}
                <span className="font-medium text-brown-900 truncate">
                  {selectedProduit.nom}
                </span>
              </div>
              <div className="text-sm text-brown-500">
                {selectedProduit.prix_unitaire.toFixed(2)} € • TVA {selectedProduit.tva}%
              </div>
            </div>
          ) : (
            <span className="text-brown-400">Sélectionner un produit...</span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-brown-400 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-beige-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Barre de recherche */}
          <div className="p-3 border-b border-beige-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brown-400" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-beige-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mint-500"
                autoFocus
              />
            </div>
          </div>

          {/* Liste des produits */}
          <div className="overflow-y-auto max-h-64">
            {isLoading ? (
              <div className="p-4 text-center text-brown-500">
                Chargement...
              </div>
            ) : produits.length === 0 ? (
              <div className="p-4 text-center text-brown-500">
                <Package className="w-8 h-8 mx-auto mb-2 text-beige-300" />
                <p>Aucun produit trouvé</p>
              </div>
            ) : (
              <>
                {/* Option pour effacer la sélection */}
                {selectedProduit && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="w-full px-4 py-2 text-left text-sm text-brown-500 hover:bg-beige-50 border-b border-beige-100"
                  >
                    ✕ Effacer la sélection
                  </button>
                )}

                {/* Liste des produits */}
                {produits.map((produit) => (
                  <button
                    key={produit.id}
                    type="button"
                    onClick={() => handleSelect(produit)}
                    className={`w-full px-4 py-3 text-left hover:bg-mint-50 border-b border-beige-100 last:border-b-0 transition-colors ${
                      selectedProduit?.id === produit.id ? 'bg-mint-100' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {produit.code && (
                            <span className="font-mono font-semibold text-mint-700 text-sm">
                              {produit.code}
                            </span>
                          )}
                          <span className="font-medium text-brown-900 truncate">
                            {produit.nom}
                          </span>
                        </div>
                        {produit.description && (
                          <div className="text-sm text-brown-500 line-clamp-2">
                            {produit.description}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="font-semibold text-brown-900">
                          {produit.prix_unitaire.toFixed(2)} €
                        </div>
                        <div className="text-xs text-brown-500">
                          TVA {produit.tva}%
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
